<template>
    <div id="app" :class="{ 'no-internet-connection': isInternetConnectionLost }">
        <div
            v-if="$route.meta.needsAuthenticatedUser === false || $route.meta.withoutWrapper"
            class="app-wrapper"
        >
            <RouterView />
        </div>
        <div v-else-if="showViewForAuthenticatedUser" class="app-wrapper">
            <AppPageWrapper>
                <AppHeader v-if="uiVisibility.header && !$route.meta.withoutHeader" />
                <AppContent :class="{ 'without-padding': withoutInnerContentPadding }">
                    <RouterView />
                    <AppConnectionLost v-if="isInternetConnectionLost" />
                </AppContent>
            </AppPageWrapper>
            <AppLoader v-if="globalLoader" class="global" />
        </div>
        <AppBackdrop :with-blur="false" />
    </div>
</template>

<script setup>
import { useStore } from 'vuex';

import AppHeader from '@/app/core/components/AppHeader/AppHeader.vue';
import AppContent from '@/app/core/components/AppContent/AppContent.vue';
import AppPageWrapper from '@/app/core/components/AppPageWrapper/AppPageWrapper.vue';
import AppLoader from '@fifa-bsm/bo-frontend-shared/components/AppLoader/AppLoader';
import AppConnectionLost from '@fifa-bsm/bo-frontend-shared/components/AppConnectionLost/AppConnectionLost';
import AppBackdrop from '@fifa-bsm/bo-frontend-shared/components/AppBackdrop/AppBackdrop';
import { listenForGlobalEchoEvents } from '@/app/core/globalEchoEventHandlers';
import { computed, onMounted, ref, watch } from 'vue';
import { useEventListener } from '@vueuse/core/index';
import { useVDestroy } from '@fifa-bsm/bo-frontend-shared/mixins';
import { useToasted } from '@zaalbarxx/vue-toasted';
import { useI18n } from '@/i18n';
import { useRoute } from 'vue-router';

const { t } = useI18n();
const store = useStore();
const toasted = useToasted();
useEventListener(window, 'resize', updateRealVh);
useEventListener(document.body, 'mousedown', enableMouseClass);
useEventListener(document.body, 'keyup', disableMouseClass);
useEventListener(window, 'online', onConnectionActive);
useEventListener(window, 'offline', onConnectionLost);
const { vDestroy } = useVDestroy();
const route = useRoute();

const initializedDataForLoggedInUser = ref(false);

const uiVisibility = computed(() => store.state.App.uiVisibility);
const user = computed(() => store.getters['User/user']);
const loadingUser = computed(() => store.getters['User/loadingUser']);
const isLoggedIn = computed(() => store.getters['Auth/isLoggedIn']);
const globalLoader = computed(() => store.getters['Config/globalLoader']);
const showViewForAuthenticatedUser = computed(() => {
    return user.value && !loadingUser.value && initializedDataForLoggedInUser.value;
});
const shouldInitializeDataForUser = computed(() => {
    return isLoggedIn.value && !initializedDataForLoggedInUser.value;
});

const isInternetConnectionLost = computed(() => store.getters['Config/connectionLost']);
const withoutInnerContentPadding = computed(() => route.meta.withoutInnerContentPadding ?? false);

watch(
    shouldInitializeDataForUser,
    (value) => {
        if (value) {
            initializeDataForLoggedInUser();
        }
    },
    { immediate: true }
);

watch(
    user,
    (value) => {
        if (value) {
            registerEchoEvents();
        }
    },
    { immediate: true }
);

onMounted(() => {
    updateRealVh();
});

function toggleMouseClass(isUsingMouse) {
    isUsingMouse
        ? document.body.classList.add('using-mouse')
        : document.body.classList.remove('using-mouse');
}

function enableMouseClass() {
    toggleMouseClass(true);
}

function disableMouseClass() {
    if (event.keyCode === 9) {
        toggleMouseClass(false);
    }
}

function updateRealVh() {
    // We execute the same script as before
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
}

function onConnectionActive() {
    store.commit('Config/setConnectionLost', false);
}

function onConnectionLost() {
    store.commit('Config/setConnectionLost', true);
}

async function initializeDataForLoggedInUser() {
    try {
        await store.dispatch('App/initialize');
    } finally {
        initializedDataForLoggedInUser.value = true;
    }
}

function registerEchoEvents() {
    listenForGlobalEchoEvents({
        user: user.value,
        toasted: toasted,
        t,
        destroy$: vDestroy,
    });
}
</script>

<style lang="scss">
@use 'sass:map';
@use './styles/main';
@use './styles/variables';

#app {
    display: flex;
    width: 100%;

    .app-wrapper {
        display: flex;
        width: 100%;
    }
}

.app-content.without-padding {
    .app-content-inner {
        padding: 0;
    }
}

.no-internet-connection .app-content {
    .app-content-inner {
        position: relative;
        overflow: auto;
        min-height: fit-content;
    }

    .app-connection-lost {
        position: absolute;
        z-index: map.get(variables.$elevation, 'connectionLostModal');
        inset: 0;
    }
}
</style>
